/**
 * sl-2q8e.2.4: useAppBaseVersioning
 *
 * React-Hook fuer AppBase Draft/Publish-Lifecycle.
 * Exposed State + Actions: saveDraft, publish, unpublish, discardDraft, restore.
 *
 * Pattern inspired by useVersionManagement + useViewerOrchestration from
 * workflow-viewer, but leaner — no graph, no versions-list UI.
 */
import { notify } from '@affine/component';
import { flushDatabaseSystemDocsForDurableWrite } from '@affine/core/modules/doc/services/database-system-doc-access';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  appBaseViewerStateQuery,
  type AppBaseViewerStateType,
  createAppBaseDraftMutation,
  discardAppBaseDraftMutation,
  publishAppBaseDraftMutation,
  restoreAppBaseVersionMutation,
  unpublishAppBaseMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { AppBaseConfigSchema } from '@streamlain/view-config';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { WorkspaceServerService } from '../../cloud';
import {
  type AppBaseVersioningAction,
  describeAppBaseVersioningError,
} from './app-base-versioning-errors';
import { usePlanQuotaRefusalActions } from './use-plan-quota-refusal-actions';

export interface UseAppBaseVersioningParams {
  workspaceId: string;
  appBaseId: string | null;
}

export interface UseAppBaseVersioningResult {
  viewerState: AppBaseViewerStateType | null;
  isLoading: boolean;
  isDirty: boolean;
  publishedVersion: number | null;
  draftVersion: number | null;
  selectedVersion: number | null;
  // sl-bpu6.16.3: Derived from backend state — `role='published' AND active=true`.
  // A row with role='published' but active=false (post-unpublish) must NOT count
  // as live, otherwise UI keeps showing "Republish" after the view was taken down.
  isLive: boolean;
  /**
   * Saves the given ViewConfig as a draft and returns the created draft
   * version. Callers that chain `saveDraft` → `publish` MUST pass this
   * returned version into `publish(version)` — the hook's captured
   * `viewerState` is stale during the same tick, so relying on the
   * fallback inside `publish()` would abort with "No draft or version
   * to publish".
   */
  saveDraft: (viewConfig: Record<string, unknown>) => Promise<number>;
  publish: (version?: number) => Promise<void>;
  unpublish: () => Promise<void>;
  discardDraft: () => Promise<void>;
  restore: (version: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAppBaseVersioning(
  params: UseAppBaseVersioningParams
): UseAppBaseVersioningResult {
  const { workspaceId, appBaseId } = params;
  const serverService = useService(WorkspaceServerService);
  const workspace = useService(WorkspaceService).workspace;
  const t = useI18n();
  const planQuotaActions = usePlanQuotaRefusalActions();
  const reportError = useCallback(
    (action: AppBaseVersioningAction, error: unknown) => {
      console.error(`[app-base-versioning] ${action} failed`, error);
      notify.error({
        ...describeAppBaseVersioningError(t, action, error),
        actions: planQuotaActions(error),
      });
    },
    [planQuotaActions, t]
  );

  const [viewerState, setViewerState] = useState<AppBaseViewerStateType | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Monotonic request id — refreshes can overlap (saveDraft kicks a refresh
  // while publish awaits its own). Only the latest request may commit state,
  // otherwise an older response can overwrite a newer one.
  const refreshRequestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++refreshRequestIdRef.current;
    if (!appBaseId) {
      // sl-bpu6.16.3 audit-pass-B: if appBaseId flips to null while
      // an older refresh is in flight the stale finally-branch is muted by
      // the request-id guard, leaving isLoading stuck on true. Clear both.
      setViewerState(null);
      setIsLoading(false);
      return;
    }
    const server = serverService.server;
    if (!server) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await server.gql({
        query: appBaseViewerStateQuery,
        variables: { workspaceId, appBaseId },
      });
      if (requestId === refreshRequestIdRef.current) {
        setViewerState(result.appBaseViewerState);
      }
    } catch (err) {
      if (requestId === refreshRequestIdRef.current) {
        reportError('load', err);
      }
    } finally {
      if (requestId === refreshRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [serverService, workspaceId, appBaseId, reportError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveDraft = useCallback(
    async (viewConfig: Record<string, unknown>): Promise<number> => {
      if (!appBaseId) {
        throw new Error('saveDraft called without appBaseId');
      }
      const server = serverService.server;
      if (!server) {
        throw new Error('saveDraft called without a server connection');
      }
      try {
        if (viewConfig.appBaseConfig != null) {
          const appConfig = AppBaseConfigSchema.parse(viewConfig.appBaseConfig);
          if (workspace.id !== workspaceId) {
            throw new Error(
              'Draft workspace does not match the active workspace.'
            );
          }
          const databasesByDoc = new Map<string, string[]>();
          for (const binding of Object.values(appConfig.databaseBindings)) {
            if (binding.status !== 'active' || !binding.exposure.materialized)
              continue;
            const ids = databasesByDoc.get(binding.placement.docId) ?? [];
            if (!ids.includes(binding.databaseId)) ids.push(binding.databaseId);
            databasesByDoc.set(binding.placement.docId, ids);
          }
          for (const [docId, databaseIds] of databasesByDoc) {
            await flushDatabaseSystemDocsForDurableWrite({
              collection: workspace.docCollection,
              docEngine: workspace.engine.doc,
              workspaceId,
              docId,
              databaseIds,
            });
          }
        }
        const result = await server.gql({
          query: createAppBaseDraftMutation,
          variables: {
            workspaceId,
            appBaseId,
            input: {
              viewConfig,
              baseVersion: viewerState?.publishedVersion ?? null,
            },
          },
        });
        // Kick off a refresh so downstream state (isDirty, draftVersion) is
        // eventually consistent, but return the fresh version immediately
        // so callers that chain publish() don't race the refresh.
        void refresh();
        return result.createAppBaseDraft.version;
      } catch (err) {
        reportError('save', err);
        throw err;
      }
    },
    [
      serverService,
      workspace,
      workspaceId,
      appBaseId,
      viewerState,
      refresh,
      reportError,
    ]
  );

  const publish = useCallback(
    async (version?: number) => {
      if (!appBaseId) return;
      const server = serverService.server;
      if (!server) return;
      const target = version ?? viewerState?.draftVersion ?? null;
      if (target === null) {
        notify.error({ title: 'No draft or version to publish' });
        return;
      }
      try {
        await server.gql({
          query: publishAppBaseDraftMutation,
          variables: { workspaceId, appBaseId, version: target },
        });
        await refresh();
      } catch (err) {
        reportError('publish', err);
        throw err;
      }
    },
    [serverService, workspaceId, appBaseId, viewerState, refresh, reportError]
  );

  const unpublish = useCallback(async () => {
    if (!appBaseId) return;
    const server = serverService.server;
    if (!server) return;
    try {
      await server.gql({
        query: unpublishAppBaseMutation,
        variables: { workspaceId, appBaseId },
      });
      await refresh();
    } catch (err) {
      reportError('unpublish', err);
      throw err;
    }
  }, [serverService, workspaceId, appBaseId, refresh, reportError]);

  const discardDraft = useCallback(async () => {
    if (!appBaseId) return;
    const server = serverService.server;
    if (!server) return;
    try {
      await server.gql({
        query: discardAppBaseDraftMutation,
        variables: { workspaceId, appBaseId },
      });
      await refresh();
    } catch (err) {
      reportError('discard', err);
      throw err;
    }
  }, [serverService, workspaceId, appBaseId, refresh, reportError]);

  const restore = useCallback(
    async (version: number) => {
      if (!appBaseId) return;
      const server = serverService.server;
      if (!server) return;
      try {
        await server.gql({
          query: restoreAppBaseVersionMutation,
          variables: { workspaceId, appBaseId, version },
        });
        await refresh();
      } catch (err) {
        reportError('restore', err);
        throw err;
      }
    },
    [serverService, workspaceId, appBaseId, refresh, reportError]
  );

  return {
    viewerState,
    isLoading,
    isDirty: viewerState?.isDirty ?? false,
    publishedVersion: viewerState?.publishedVersion ?? null,
    draftVersion: viewerState?.draftVersion ?? null,
    selectedVersion: viewerState?.selectedVersion ?? null,
    isLive: viewerState?.isLive ?? false,
    saveDraft,
    publish,
    unpublish,
    discardDraft,
    restore,
    refresh,
  };
}
